// /optimize/impact — Owen → Execute → Grace longitudinal results.
// Closes the feedback loop: did Owen's interventions actually move the
// rank? Reads optimization_attempts + the closest serp_snapshots to
// compute post-attempt position delta at 7d / 14d / 28d.
//
// Operator's questions answered here:
//   1. Total Owen Execute attempts + how many hit top-10
//   2. Avg position movement across all attempts (negative = improvement)
//   3. Top movers — biggest improvements + biggest regressions
//   4. Full attempt history with grade, pre/post position, run links

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { optimizeApi, type OptimizationAttempt } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function OptimizeImpactPage() {
  const q = useQuery({
    queryKey: ["optimize-impact"],
    queryFn: optimizeApi.impact,
    refetchInterval: 5 * 60_000,
  });

  if (q.isLoading) return <div className="text-sm text-ink-500">Loading impact data…</div>;
  if (q.error) return <div className="text-sm text-red-700">Error: {String((q.error as Error).message)}</div>;
  if (!q.data) return null;

  const { kpis, topImprovers, topRegressors, attempts } = q.data;
  const empty = kpis.total === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink-900">Optimization impact</h1>
          <p className="text-sm text-ink-500 mt-1">
            Owen → Execute → Grace cycles, with rank-movement deltas from Spencer's weekly probes.{" "}
            <span className="text-ink-400">
              KPIs show <strong>current</strong> attempts only — when a (site, keyword) is regenerated, earlier attempts
              are overwritten on the live page and marked <em>superseded</em> below.
            </span>
          </p>
        </div>
        <Link
          to="/optimize"
          className="text-xs px-3 py-1.5 rounded border border-ink-200 text-ink-700 hover:bg-ink-50"
        >
          ← Back to optimization queue
        </Link>
      </div>

      {empty ? (
        <div className="bg-white border border-dashed border-ink-200 rounded-xl p-12 text-center text-ink-500">
          <p className="text-base mb-2">No optimization attempts on record yet.</p>
          <p className="text-sm">
            When you click Execute on an Owen brief in the <Link to="/inbox" className="text-accent underline">inbox</Link>,
            Grace will grade the result and the attempt will appear here.
          </p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi
              label="Current attempts"
              value={String(kpis.total)}
              sub={kpis.supersededCount > 0 ? `+${kpis.supersededCount} superseded` : undefined}
              accent="ink"
            />
            <Kpi
              label="Top-10 hits"
              value={`${kpis.top10Hits}/${kpis.total}`}
              sub={kpis.total > 0 ? `${Math.round((kpis.top10Hits / kpis.total) * 100)}%` : undefined}
              accent="green"
            />
            <Kpi
              label="Improving"
              value={String(kpis.improving)}
              sub={kpis.improving + kpis.regressing > 0 ? `vs ${kpis.regressing} regressing` : undefined}
              accent="green"
            />
            <Kpi
              label="Avg Δ @ 14d"
              value={kpis.avgDelta14 != null ? formatDelta(kpis.avgDelta14) : "—"}
              sub="(negative = better)"
              accent={kpis.avgDelta14 != null && kpis.avgDelta14 < 0 ? "green" : kpis.avgDelta14 != null && kpis.avgDelta14 > 0 ? "red" : "ink"}
            />
            <Kpi
              label="Awaiting"
              value={String(kpis.awaiting)}
              sub="(< 7d post)"
              accent="amber"
            />
          </div>

          {/* Grade distribution */}
          <div className="bg-white rounded-xl border border-ink-100 p-5">
            <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">Grade distribution</h2>
            <GradeBar buckets={kpis.gradeBuckets} total={kpis.total} />
          </div>

          {/* Top movers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-ink-100 p-5">
              <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
                ↑ Top improvers (by Δ @ 14d)
              </h2>
              {topImprovers.length === 0 ? (
                <p className="text-sm text-ink-500 italic">No improvers yet — most attempts still awaiting their 14d probe.</p>
              ) : (
                <AttemptsTable rows={topImprovers} compact />
              )}
            </div>
            <div className="bg-white rounded-xl border border-ink-100 p-5">
              <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
                ↓ Top regressors (by Δ @ 14d)
              </h2>
              {topRegressors.length === 0 ? (
                <p className="text-sm text-ink-500 italic">No regressions detected — celebrate quietly.</p>
              ) : (
                <AttemptsTable rows={topRegressors} compact />
              )}
            </div>
          </div>

          {/* All attempts */}
          <div className="bg-white rounded-xl border border-ink-100 p-5">
            <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
              All attempts ({attempts.length})
            </h2>
            <AttemptsTable rows={attempts} />
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent }: {
  label: string; value: string; sub?: string;
  accent: "ink" | "green" | "amber" | "red";
}) {
  const cls =
    accent === "green" ? "border-green-200 bg-green-50"
    : accent === "amber" ? "border-amber-200 bg-amber-50"
    : accent === "red" ? "border-red-200 bg-red-50"
    : "border-ink-100 bg-white";
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">{label}</div>
      <div className="text-2xl font-display font-bold text-ink-900 leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-ink-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function GradeBar({ buckets, total }: { buckets: { A: number; B: number; C: number; D: number; F: number; ungraded: number }; total: number }) {
  const pct = (n: number) => total > 0 ? `${(n / total * 100).toFixed(0)}%` : "0%";
  return (
    <div className="space-y-2">
      <div className="flex h-3 rounded-md overflow-hidden border border-ink-200">
        {buckets.A > 0 && <div className="bg-green-600" style={{ width: pct(buckets.A) }} title={`${buckets.A} graded A`} />}
        {buckets.B > 0 && <div className="bg-green-400" style={{ width: pct(buckets.B) }} title={`${buckets.B} graded B`} />}
        {buckets.C > 0 && <div className="bg-amber-300" style={{ width: pct(buckets.C) }} title={`${buckets.C} graded C`} />}
        {buckets.D > 0 && <div className="bg-amber-500" style={{ width: pct(buckets.D) }} title={`${buckets.D} graded D`} />}
        {buckets.F > 0 && <div className="bg-red-500" style={{ width: pct(buckets.F) }} title={`${buckets.F} graded F`} />}
        {buckets.ungraded > 0 && <div className="bg-ink-300" style={{ width: pct(buckets.ungraded) }} title={`${buckets.ungraded} ungraded`} />}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-ink-600">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-600"></span>A {buckets.A}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-400"></span>B {buckets.B}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-300"></span>C {buckets.C}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500"></span>D {buckets.D}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500"></span>F {buckets.F}</span>
        {buckets.ungraded > 0 && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-ink-300"></span>Ungraded {buckets.ungraded}</span>}
      </div>
    </div>
  );
}

export function AttemptsTable({ rows, compact }: { rows: OptimizationAttempt[]; compact?: boolean }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<OptimizationAttempt>(
    rows,
    {
      site: (a) => a.domain,
      keyword: (a) => a.keyword,
      pre: (a) => a.prePosition,
      d7: (a) => a.postPositionD7,
      d14: (a) => a.postPositionD14,
      d28: (a) => a.postPositionD28,
      latest: (a) => a.latestPosition,
      delta14: (a) => a.delta14,
      status: (a) => a.movement,
      grade: (a) => a.gradeScore,
      when: (a) => a.attemptedAt,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
          <tr>
            {!compact && <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 pr-3" />}
            <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left py-2 px-3" />
            <SortHeader label="Pre" sortKey="pre" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Position when Owen briefed this attempt" />
            <SortHeader label="d7" sortKey="d7" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Closest probe ~7 days post-attempt" />
            <SortHeader label="d14" sortKey="d14" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Closest probe ~14 days post-attempt — primary movement signal" />
            <SortHeader label="d28" sortKey="d28" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Closest probe ~28 days post-attempt" />
            <SortHeader label="Latest" sortKey="latest" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" title="Most recent Spencer probe" />
            <SortHeader label="Δ 14d" sortKey="delta14" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="Status" sortKey="status" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            <SortHeader label="Grade" sortKey="grade" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-center py-2 px-3" />
            {!compact && <SortHeader label="When" sortKey="when" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right py-2 pl-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((a) => <AttemptRow key={a.id} attempt={a} compact={compact ?? false} />)}
        </tbody>
      </table>
    </div>
  );
}

function AttemptRow({ attempt, compact }: { attempt: OptimizationAttempt; compact: boolean }) {
  const delta14 = attempt.delta14 ?? null;
  const movement = attempt.movement;
  const superseded = attempt.isCurrent === false;
  // Superseded rows = the homepage was redeployed-over by a later
  // regen on the same (site, keyword). Visually dimmed so operator
  // can scan past them when looking for current state.
  const rowCls = superseded
    ? "hover:bg-ink-50/40 opacity-60"
    : "hover:bg-ink-50/40";
  return (
    <tr className={rowCls}>
      {!compact && (
        <td className="py-2 pr-3">
          {attempt.siteId ? (
            <Link to={`/sites/${attempt.siteId}`} className="text-accent hover:underline">
              {attempt.domain ?? attempt.siteId.slice(0, 8)}
            </Link>
          ) : <span className="text-ink-500">—</span>}
        </td>
      )}
      <td className={"py-2 px-3 text-ink-700 " + (superseded ? "line-through" : "")}>{attempt.keyword}</td>
      <td className="py-2 px-3 text-center font-mono text-ink-700">{attempt.prePosition ?? "—"}</td>
      <td className="py-2 px-3 text-center font-mono text-ink-700">
        <ProbeWindowCell value={attempt.postPositionD7} windowDays={7} attemptedAt={attempt.attemptedAt} />
      </td>
      <td className="py-2 px-3 text-center font-mono text-ink-900 font-semibold">
        <ProbeWindowCell value={attempt.postPositionD14} windowDays={14} attemptedAt={attempt.attemptedAt} primary />
      </td>
      <td className="py-2 px-3 text-center font-mono text-ink-700">
        <ProbeWindowCell value={attempt.postPositionD28} windowDays={28} attemptedAt={attempt.attemptedAt} />
      </td>
      <td className="py-2 px-3 text-center font-mono text-ink-700">{attempt.latestPosition ?? "—"}</td>
      <td className="py-2 px-3 text-center">
        {delta14 != null ? (
          <span className={delta14 < 0 ? "text-green-700 font-semibold" : delta14 > 0 ? "text-red-700 font-semibold" : "text-ink-500"}>
            {formatDelta(delta14)}
          </span>
        ) : <span className="text-ink-400">—</span>}
      </td>
      <td className="py-2 px-3 text-center">
        <MovementBadge movement={movement} />
      </td>
      <td className="py-2 px-3 text-center">
        <GradeBadge letter={attempt.gradeLetter} score={attempt.gradeScore} />
      </td>
      {!compact && (
        <td className="py-2 pl-3 text-right text-xs text-ink-500 whitespace-nowrap">
          {attempt.threadKey ? (
            <Link to={`/inbox?thread=${encodeURIComponent(attempt.threadKey)}`} className="text-accent hover:underline">
              {timeAgo(attempt.attemptedAt)}
            </Link>
          ) : timeAgo(attempt.attemptedAt)}
        </td>
      )}
    </tr>
  );
}

/**
 * Render a post-execute probe cell (d7 / d14 / d28). When Spencer
 * has captured a rank, show the position. When the window is still
 * open (we're within N days of the attempt), show "in Xd" so the
 * operator knows the data isn't missing — Spencer just hasn't
 * captured yet. Only show "—" once the window has fully passed.
 */
function ProbeWindowCell({
  value,
  windowDays,
  attemptedAt,
  primary,
}: {
  value: number | null | undefined;
  windowDays: number;
  attemptedAt: string;
  primary?: boolean;
}) {
  if (value != null) return <span>{value}</span>;
  // Capture target = T+24h; full window = T + windowDays.
  const attemptedMs = new Date(attemptedAt).getTime();
  const elapsedHours = (Date.now() - attemptedMs) / (1000 * 60 * 60);
  const windowHours = windowDays * 24;
  if (elapsedHours >= windowHours) return <span className="text-ink-400">—</span>;
  const POST_EXEC_DELAY_HOURS = 24; // matches Spencer T2 trigger
  if (elapsedHours < POST_EXEC_DELAY_HOURS) {
    const hoursLeft = Math.max(1, Math.ceil(POST_EXEC_DELAY_HOURS - elapsedHours));
    return (
      <span
        className={`text-[10px] ${primary ? "text-amber-700 font-medium" : "text-amber-600"} normal-case`}
        title={`Spencer captures the post-attempt rank ~24h after Execute completes. This attempt was ${Math.round(elapsedHours)}h ago.`}
      >
        probe in {hoursLeft}h
      </span>
    );
  }
  // Past 24h, within window — just waiting for the next Spencer
  // probe cycle to land in this window.
  const daysToWindow = Math.max(1, Math.ceil((windowHours - elapsedHours) / 24));
  return (
    <span
      className={`text-[10px] ${primary ? "text-blue-700 font-medium" : "text-blue-600"} normal-case`}
      title={`Spencer's last probe didn't land in this window yet. Day ${windowDays} closes in ${daysToWindow}d.`}
    >
      window {daysToWindow}d
    </span>
  );
}

function MovementBadge({ movement }: { movement?: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    top10_hit: { cls: "bg-green-100 text-green-800 border-green-200", label: "Top-10 ✓" },
    improving: { cls: "bg-green-50 text-green-700 border-green-200", label: "Improving" },
    flat: { cls: "bg-ink-100 text-ink-700 border-ink-200", label: "Flat" },
    regressing: { cls: "bg-red-50 text-red-700 border-red-200", label: "Regressing" },
    awaiting: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Awaiting" },
    superseded: { cls: "bg-ink-50 text-ink-500 border-ink-200 italic", label: "Superseded" },
  };
  const m = map[movement ?? "awaiting"]!;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
}

function GradeBadge({ letter, score }: { letter: string | null; score: number | null }) {
  if (!letter) return <span className="text-ink-400 text-xs">—</span>;
  const cls =
    letter === "A" ? "bg-green-600 text-white"
    : letter === "B" ? "bg-green-500 text-white"
    : letter === "C" ? "bg-amber-400 text-ink-900"
    : letter === "D" ? "bg-amber-600 text-white"
    : "bg-red-600 text-white";
  return (
    <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded text-xs font-bold ${cls}`} title={score != null ? `Score: ${score}%` : undefined}>
      {letter}
    </span>
  );
}

function formatDelta(n: number): string {
  if (n === 0) return "0";
  const rounded = Math.round(n * 10) / 10;
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 60) return min < 1 ? "now" : `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
